import CollectionSlider from "@/components/molecules/collection-slider";
import InspirationImage from "@/components/molecules/inspiration-image";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { TablesDocument } from "@/prismicio-types";
import Link from "next/link";

type TablesCollectionProps = {
  tables: TablesDocument[];
  locale: Locales;
  variant?: "default" | "table";
  className?: string;
};

const TablesCollection = ({ tables, locale, variant = "default", className }: TablesCollectionProps) => {
  const tableItems = tables || [];

  const renderOneItem = () => (
    <div className="col-span-2">
      <InspirationImage
        locale={locale}
        image={tableItems[0].data.featured_image.url || ""}
        imageAlt={tableItems[0].data.featured_image.alt || ""}
        link={`${getPermalink(locale, "tables")}/${tableItems.at(0)?.uid}`}
        variant="default"
        category={__("See used items", locale)}
      />
    </div>
  );

  const renderTwoItems = () => (
    <>
      <InspirationImage
        locale={locale}
        image={tableItems[0].data.featured_image.url || ""}
        imageAlt={tableItems[0].data.featured_image.alt || ""}
        link={`${getPermalink(locale, "tables")}/${tableItems.at(0)?.uid}`}
        variant="default"
        category={__("See used items", locale)}
      />
      <InspirationImage
        locale={locale}
        image={tableItems[1].data.featured_image.url || ""}
        imageAlt={tableItems[1].data.featured_image.alt || ""}
        link={`${getPermalink(locale, "tables")}/${tableItems.at(1)?.uid}`}
        variant="default"
        category={__("See used items", locale)}
      />
    </>
  );

  const renderThreeItems = () => (
    <>
      <InspirationImage
        locale={locale}
        variant="default"
        image={tableItems[0].data.featured_image.url || ""}
        imageAlt={tableItems[0].data.featured_image.alt || ""}
        link={`${getPermalink(locale, "tables")}/${tableItems.at(0)?.uid}`}
        category={__("See used items", locale)}
      />
      <div className="grid grid-cols-1 gap-5">
        <InspirationImage
          locale={locale}
          image={tableItems[1].data.featured_image.url || ""}
          imageAlt={tableItems[1].data.featured_image.alt || ""}
          link={`${getPermalink(locale, "tables")}/${tableItems.at(1)?.uid}`}
          variant="default"
          className="md:aspect-[unset]"
          category={__("See used items", locale)}
        />
        <InspirationImage
          locale={locale}
          image={tableItems[2].data.featured_image.url || ""}
          imageAlt={tableItems[2].data.featured_image.alt || ""}
          link={`${getPermalink(locale, "tables")}/${tableItems.at(2)?.uid}`}
          variant="default"
          className="md:aspect-[unset]"
          category={__("See used items", locale)}
        />
      </div>
    </>
  );

  const renderFourOrMoreItems = () => {
    // Convert remaining items (after first 2) to slider format
    const sliderItems = tableItems.slice(2).map((item) => ({
      image: item.data.featured_image.url || "",
      tag: __("See used items", locale),
    }));

    return (
      <>
        <CollectionSlider locale={locale} collections={sliderItems} />
        <div className="grid grid-cols-1 gap-5">
          <InspirationImage
            locale={locale}
            image={tableItems[0].data.featured_image.url || ""}
            imageAlt={tableItems[0].data.featured_image.alt || ""}
            link={`${getPermalink(locale, "tables")}/${tableItems.at(0)?.uid}`}
            variant="default"
            className="md:aspect-[unset]"
            category={__("See used items", locale)}
          />
          <InspirationImage
            locale={locale}
            image={tableItems[1].data.featured_image.url || ""}
            imageAlt={tableItems[1].data.featured_image.alt || ""}
            link={`${getPermalink(locale, "tables")}/${tableItems.at(1)?.uid}`}
            variant="default"
            className="md:aspect-[unset]"
            category={__("See used items", locale)}
          />
        </div>
      </>
    );
  };

  const renderContent = () => {
    const itemCount = tableItems.length;

    if (itemCount === 0) return null;
    if (itemCount === 1) return renderOneItem();
    if (itemCount === 2) return renderTwoItems();
    if (itemCount === 3) return renderThreeItems();
    return renderFourOrMoreItems();
  };

  // Don't render if no items
  if (!tableItems || tableItems.length === 0) {
    return null;
  }

  return (
    <section
      className={cn(
        "container hidden grid-cols-1 gap-5 px-4 py-6 min-[925px]:grid md:grid-cols-2 md:px-7.5",
        className
      )}
    >
      <header
        className={cn(
          "col-span-2 flex flex-col items-center justify-between gap-3 md:flex-row",
          variant === "table" && "md:flex-col"
        )}
      >
        {variant === "default" ? (
          <>
            <h2 className="font-cabinet-grotesk text-center text-3xl font-medium">{__("Our tables", locale)}</h2>
            <Link
              className="text-dark-primary focus-visible:ring-dark-primary/70 rounded-sm text-base font-medium underline focus-visible:ring-[3px] focus-visible:ring-offset-3 focus-visible:outline-none md:uppercase"
              href={getPermalink(locale, "tables")}
            >
              {__("See all tables", locale)}
            </Link>
          </>
        ) : (
          <h2 className="font-cabinet-grotesk text-center">{__("Discover other “Tables”", locale)}</h2>
        )}
      </header>

      {renderContent()}
    </section>
  );
};

export default TablesCollection;
